import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Heart, MessageCircle, Share2, MoreHorizontal, MapPin, Calendar } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

interface PhotoFeedPost {
  id: string;
  user: {
    id: string;
    name: string;
    avatar: string;
    verified: boolean;
    sport?: string;
  };
  content: string;
  photos: string[];
  location?: string;
  tags: string[];
  category: string;
  likes: number;
  comments: number;
  isLiked: boolean;
  createdAt: string;
}

interface PhotoFeedProps {
  posts: PhotoFeedPost[];
  onLike: (postId: string) => void;
  onComment: (postId: string) => void;
  onShare: (postId: string) => void;
}

const PhotoFeed = ({ posts, onLike, onComment, onShare }: PhotoFeedProps) => {
  const { user } = useAuth();
  const [expandedPosts, setExpandedPosts] = useState<Set<string>>(new Set());

  const toggleExpanded = (postId: string) => {
    const newExpanded = new Set(expandedPosts);
    if (newExpanded.has(postId)) {
      newExpanded.delete(postId);
    } else {
      newExpanded.add(postId);
    }
    setExpandedPosts(newExpanded);
  };

  const PhotoGrid = ({ photos, postId }: { photos: string[]; postId: string }) => {
    const isExpanded = expandedPosts.has(postId);
    const displayPhotos = isExpanded ? photos : photos.slice(0, 4);
    const remainingCount = photos.length - 4;

    return (
      <div className="mt-3">
        <div className={`grid gap-1 ${
          displayPhotos.length === 1 ? 'grid-cols-1' :
          displayPhotos.length === 2 ? 'grid-cols-2' :
          displayPhotos.length === 3 ? 'grid-cols-3' :
          'grid-cols-2'
        }`}>
          {displayPhotos.map((photo, index) => (
            <div 
              key={index} 
              className={`relative overflow-hidden rounded-lg ${
                displayPhotos.length === 1 ? 'aspect-video' :
                displayPhotos.length === 3 && index === 0 ? 'col-span-2 aspect-video' :
                'aspect-square'
              }`}
            >
              <img
                src={photo}
                alt={`Foto ${index + 1}`}
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-200 cursor-pointer"
                onClick={() => {/* Open photo modal */}}
              />
              
              {/* Show remaining count on last photo */}
              {!isExpanded && index === 3 && remainingCount > 0 && (
                <div 
                  className="absolute inset-0 bg-black bg-opacity-60 flex items-center justify-center cursor-pointer"
                  onClick={() => toggleExpanded(postId)}
                >
                  <span className="text-white text-xl font-bold">+{remainingCount}</span>
                </div>
              )}
            </div>
          ))}
        </div>
        
        {photos.length > 4 && (
          <button
            onClick={() => toggleExpanded(postId)}
            className="mt-2 text-green-500 hover:text-green-600 text-sm font-medium"
          >
            {isExpanded ? 'Ver menos' : `Ver todas las fotos (${photos.length})`}
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {posts.map((post) => (
        <div key={post.id} className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
          {/* Post Header */}
          <div className="p-4 flex items-start justify-between">
            <div className="flex items-center space-x-3">
              <Link to={`/profile/${post.user.id}`} className="flex-shrink-0">
                <div className="w-10 h-10 rounded-full overflow-hidden">
                  <img 
                    src={post.user.avatar} 
                    alt={post.user.name} 
                    className="w-full h-full object-cover"
                  />
                </div>
              </Link>
              <div>
                <div className="flex items-center">
                  <Link 
                    to={`/profile/${post.user.id}`} 
                    className="font-medium text-gray-900 dark:text-white hover:underline"
                  >
                    {post.user.name}
                  </Link>
                  {post.user.verified && (
                    <svg className="w-4 h-4 text-blue-500 ml-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
                <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 space-x-2">
                  <span>{post.user.sport}</span>
                  <span>•</span>
                  <div className="flex items-center">
                    <Calendar size={12} className="mr-1" />
                    {new Date(post.createdAt).toLocaleDateString()}
                  </div>
                  {post.location && (
                    <>
                      <span>•</span>
                      <div className="flex items-center">
                        <MapPin size={12} className="mr-1" />
                        {post.location}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
            <button className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 transition-colors duration-200">
              <MoreHorizontal size={20} />
            </button>
          </div>
          
          {/* Post Content */}
          {post.content && (
            <div className="px-4 pb-2">
              <p className="text-gray-800 dark:text-gray-200 whitespace-pre-line">
                {post.content}
              </p>
            </div>
          )}

          {/* Tags */}
          {post.tags.length > 0 && (
            <div className="px-4 pb-2">
              <div className="flex flex-wrap gap-1">
                {post.tags.map((tag, index) => (
                  <span key={index} className="text-green-500 hover:text-green-600 text-sm cursor-pointer">
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {/* Photos */}
          <PhotoGrid photos={post.photos} postId={post.id} />
          
          {/* Post Stats */}
          <div className="px-4 py-2 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 mt-2">
            <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
              <span>{post.likes} likes</span>
              <span>{post.comments} comentarios</span>
            </div>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {post.category}
            </span>
          </div>
          
          {/* Post Actions */}
          <div className="px-4 py-2 flex items-center justify-between border-t border-gray-200 dark:border-gray-700">
            <button 
              onClick={() => onLike(post.id)}
              className={`flex items-center space-x-1 px-3 py-1 rounded-md transition-colors duration-200 ${
                post.isLiked 
                  ? 'text-red-500 dark:text-red-400' 
                  : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <Heart size={18} fill={post.isLiked ? 'currentColor' : 'none'} />
              <span>Me gusta</span>
            </button>
            <button 
              onClick={() => onComment(post.id)}
              className="flex items-center space-x-1 px-3 py-1 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors duration-200"
            >
              <MessageCircle size={18} />
              <span>Comentar</span>
            </button>
            <button 
              onClick={() => onShare(post.id)}
              className="flex items-center space-x-1 px-3 py-1 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors duration-200"
            >
              <Share2 size={18} />
              <span>Compartir</span>
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default PhotoFeed;